import { useEffect, useRef, useState } from 'react';
import { Camera, Loader2 } from 'lucide-react';

const gbp = (p) => `£${((p || 0) / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * "Captures awaiting this invoice" — provisional capture rows for the same
 * supplier + job (last 90 days). Ticked rows are marked covered when the
 * invoice is posted, so the job never counts the counter capture AND the
 * invoice line.
 */
export function CoverCapturesPanel({ api, itemId, lines, form, readOnly, item, selected, setSelected }) {
  const [cands, setCands] = useState(null);
  const [loading, setLoading] = useState(false);
  const seeded = useRef(false);
  const projectIds = [...new Set(lines.map(l => l.project_id).filter(Boolean))].join(',');
  const gross = form.net && parseFloat(form.net) > 0 ? (parseFloat(form.net) + (parseFloat(form.vat) || 0)).toFixed(2) : null;
  const amounts = [...new Set([form.net, gross, ...lines.map(l => l.amount)].filter(v => v && parseFloat(v) > 0))].join(',');
  const key = `${projectIds}|${form.supplier_name}|${form.reference}|${amounts}`;

  useEffect(() => {
    if (readOnly || !projectIds || !form.supplier_name) { setCands([]); return; }
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/cost-inbox/${itemId}/capture-candidates`, { params: { project_ids: projectIds, supplier_name: form.supplier_name, reference: form.reference || undefined, amounts: amounts || undefined } });
        if (!alive) return;
        const list = r.data?.candidates || [];
        setCands(list);
        if (!seeded.current) { seeded.current = true; setSelected(list.filter(c => c.suggested).map(c => c.id)); }
        else setSelected(sel => sel.filter(id => list.some(c => c.id === id)));
      } catch { if (alive) setCands([]); }
      finally { if (alive) setLoading(false); }
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [api, itemId, key, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  if (readOnly) {
    const n = item?.covered_capture_ids?.length || 0;
    return n ? <div className="text-xs text-emerald-300" data-testid="inbox-covered-info">Covered {n} counter capture{n === 1 ? '' : 's'} on the job</div> : null;
  }
  if (!cands || cands.length === 0) return null;
  const toggle = (id) => setSelected(sel => (sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]));
  const total = cands.filter(c => selected.includes(c.id)).reduce((s, c) => s + (c.value_pence || 0), 0);

  return (
    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2" data-testid="inbox-cover-captures">
      <div className="flex items-center gap-2 text-sm font-semibold text-emerald-200">
        <Camera className="w-4 h-4" /> Captures awaiting this invoice
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
        <span className="ml-auto text-xs font-normal text-gray-400">{selected.length} of {cands.length} ticked · {gbp(total)}</span>
      </div>
      <p className="text-[11px] text-gray-400">Same supplier and job in the last 90 days. Ticked captures are marked <b className="text-emerald-300">covered</b> when you post — the invoice line replaces them in the job's spend.</p>
      <div className="space-y-1">
        {cands.map(c => (
          <label key={c.id} className={`flex items-center gap-3 rounded-md px-2 py-1.5 cursor-pointer ${selected.includes(c.id) ? 'bg-emerald-500/10' : 'hover:bg-white/5'}`} data-testid={`inbox-cover-row-${c.id}`}>
            <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} className="accent-[#F5A623]" data-testid={`inbox-cover-check-${c.id}`} />
            <div className="min-w-0 flex-1 text-xs">
              <div className="text-white truncate">
                {c.project_ref?.split('-')[0]} · {c.description || c.reference || c.category}
                {c.details_only && <span className="ml-1.5 text-[10px] text-gray-400 uppercase">details only</span>}
              </div>
              <div className="text-gray-400">{c.created_at?.slice(0, 10).split('-').reverse().join('/')} · {c.captured_by_name}{c.reference ? ` · ref ${c.reference}` : ''}{c.suggested && <span className="ml-1.5 text-emerald-300">· matches {c.match_reason}</span>}</div>
            </div>
            <div className="text-sm font-semibold text-white tabular-nums">{c.value_pending ? <span className="text-amber-300 text-xs">pending</span> : gbp(c.value_pence)}</div>
          </label>
        ))}
      </div>
    </div>
  );
}
